// SnowClub sign-up age gate and agreement (youth spec v1.1; founder rulings 2026-10-20 21:14 + 21:29), shared by the Root and Accounts
// sign-up forms and their proxies. Pure: no I/O, no React, the clock is passed in.
//
// MIRRORS THE BACKEND (snowclub-be src/helpers/member-age.ts and signup-agreement.ts, docs/b3-settings-privacy-contract.md §S); the backend
// stays the authority and refuses anything this lets through.
//   - Month and year only, never a day. "Certainly reached age N" holds only from the first day of the month AFTER the birthday month, N years
//     on (UTC), so the 13th-birthday month is still under 13 and the 16th-birthday month is still youth.
//   - Under 13 creates nothing: the form shows "ask a parent" and sends, stores and logs nothing about the visitor.
//   - The agreement is whatever GET /api/auth/signup-agreement serves: the label and the three exact document version ids. Nothing here invents
//     a version; a contract that does not parse is unusable and the form cannot be submitted.
export const SIGNUP_MINIMUM_AGE = 13;
export const YOUTH_UNTIL_AGE = 16;
const MIN_BIRTH_YEAR = 1900;

export type SignupAgeBand = "under13" | "youth" | "member";

const monthIndex = (year: number, month: number) => year * 12 + (month - 1);
const nowIndex = (nowMs: number) => {
  const d = new Date(nowMs);
  return monthIndex(d.getUTCFullYear(), d.getUTCMonth() + 1);
};

/** The birth month (1-12) and year as JSON integers, not before 1900 and not in the future; null otherwise. */
export function parseBirth(month: unknown, year: unknown, nowMs: number): { month: number; year: number } | null {
  if (typeof month !== "number" || typeof year !== "number" || !Number.isInteger(month) || !Number.isInteger(year)) return null;
  if (month < 1 || month > 12 || year < MIN_BIRTH_YEAR || monthIndex(year, month) > nowIndex(nowMs)) return null;
  return { month, year };
}

/** true only when the person has reached `age` whatever day of the birth month they were born. */
export const certainlyAtLeast = (month: number, year: number, age: number, nowMs: number) =>
  nowIndex(nowMs) - monthIndex(year, month) > age * 12;

/** The sign-up band for a birth month/year, or null when they are not a valid pair. `minimumAge` is the served contract's (never below 13). */
export function signupAgeBand(month: unknown, year: unknown, nowMs: number, minimumAge = SIGNUP_MINIMUM_AGE): SignupAgeBand | null {
  const birth = parseBirth(month, year, nowMs);
  if (!birth) return null;
  if (!certainlyAtLeast(birth.month, birth.year, Math.max(SIGNUP_MINIMUM_AGE, minimumAge), nowMs)) return "under13";
  return certainlyAtLeast(birth.month, birth.year, YOUTH_UNTIL_AGE, nowMs) ? "member" : "youth";
}

export const BIRTH_MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"] as const;
/** Every selectable year, newest first, with no default and no hint of which ages are accepted. */
export function birthYears(nowMs: number): number[] {
  const out: number[] = [];
  for (let y = new Date(nowMs).getUTCFullYear(); y >= MIN_BIRTH_YEAR; y--) out.push(y);
  return out;
}

// ── The served agreement ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────────
export const AGREEMENT_DOCS = [
  { kind: "snowclub_terms", field: "termsVersion", slug: "terms", phrase: "Terms of use" },
  { kind: "snowclub_membership", field: "membershipVersion", slug: "membership", phrase: "Membership terms" },
  { kind: "snowclub_privacy", field: "privacyVersion", slug: "privacy", phrase: "Privacy policy" },
] as const;
export type AgreementKind = (typeof AGREEMENT_DOCS)[number]["kind"];
export type AgreementField = (typeof AGREEMENT_DOCS)[number]["field"];
export type AgreementSlug = (typeof AGREEMENT_DOCS)[number]["slug"];

export interface SignupAgreement {
  status: string;
  minimumAge: number;
  agreementLabel: string;
  /** the version id per submitted field, exactly as served */
  versions: Record<AgreementField, string>;
}

const VERSION_RE = /^[0-9a-f]{64}$/;
const isObj = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);
const isText = (v: unknown, max: number): v is string => typeof v === "string" && v.trim() !== "" && v.length <= max;

/** The served GET /api/auth/signup-agreement body, or null when any part is missing or not the shape the backend documents. */
export function parseSignupAgreement(raw: unknown): SignupAgreement | null {
  if (!isObj(raw) || !isText(raw.status, 64) || !isText(raw.agreementLabel, 300) || !Array.isArray(raw.documents)) return null;
  if (typeof raw.minimumAge !== "number" || !Number.isInteger(raw.minimumAge) || raw.minimumAge < SIGNUP_MINIMUM_AGE) return null;
  if (raw.documents.length !== AGREEMENT_DOCS.length) return null;
  const versions: Partial<Record<AgreementField, string>> = {};
  for (const doc of AGREEMENT_DOCS) {
    const served = raw.documents.filter((d): d is Record<string, unknown> => isObj(d) && d.kind === doc.kind);
    const d = served.length === 1 ? served[0] : undefined;
    if (!d || d.field !== doc.field || d.link !== `/${doc.slug}` || !isText(d.title, 200) || typeof d.versionId !== "string" || !VERSION_RE.test(d.versionId)) return null;
    versions[doc.field] = d.versionId;
  }
  return { status: raw.status, minimumAge: raw.minimumAge, agreementLabel: raw.agreementLabel, versions: versions as Record<AgreementField, string> };
}

/** The label split into plain text and the three document names, each to be drawn as a link to its public page. Ends with a full stop. */
export function agreementSegments(label: string): ({ text: string } | { text: string; slug: AgreementSlug })[] {
  const full = /[.!?]$/.test(label) ? label : `${label}.`;
  const out: ({ text: string } | { text: string; slug: AgreementSlug })[] = [];
  let rest = full;
  for (;;) {
    let next: { at: number; doc: (typeof AGREEMENT_DOCS)[number] } | null = null;
    for (const doc of AGREEMENT_DOCS) {
      const at = rest.indexOf(doc.phrase);
      if (at >= 0 && (!next || at < next.at)) next = { at, doc };
    }
    if (!next) break;
    if (next.at > 0) out.push({ text: rest.slice(0, next.at) });
    out.push({ text: next.doc.phrase, slug: next.doc.slug });
    rest = rest.slice(next.at + next.doc.phrase.length);
  }
  if (rest) out.push({ text: rest });
  return out;
}

// ── The register body's gate fields ──────────────────────────────────────────────────────────────────────────────────────────────────────────
export interface SignupGateFields {
  birthMonth: number;
  birthYear: number;
  agreementAccepted: true;
  termsVersion: string;
  privacyVersion: string;
  membershipVersion: string;
}
export const AGREE_REQUIRED = "Tick the box to confirm you're 13 or older and agree to the SnowClub Terms of use, Membership terms and Privacy policy.";
export const BIRTH_REQUIRED = "Enter your birth month and year.";
export const ASK_A_PARENT = "Ask a parent to add you";

/** The gate fields a proxy forwards, checked strictly (JSON integers, exactly `true`, well-formed ids), or why the body is refused. */
export function gateFields(b: Record<string, unknown>, nowMs: number): { fields: SignupGateFields; band: Exclude<SignupAgeBand, "under13"> } | { status: 400 | 403; error: string } {
  const band = signupAgeBand(b.birthMonth, b.birthYear, nowMs);
  if (band === null) return { status: 400, error: BIRTH_REQUIRED };
  if (band === "under13") return { status: 403, error: ASK_A_PARENT };
  if (b.agreementAccepted !== true) return { status: 400, error: AGREE_REQUIRED };
  const versions: Partial<Record<AgreementField, string>> = {};
  for (const { field } of AGREEMENT_DOCS) {
    const v = b[field];
    if (typeof v !== "string" || !VERSION_RE.test(v)) return { status: 400, error: "The agreement has changed. Review it and tick the box again." };
    versions[field] = v;
  }
  if (b.marketingEmail !== undefined && typeof b.marketingEmail !== "boolean") return { status: 400, error: "Invalid member-deals email choice." };
  if (band === "youth" && b.marketingEmail === true) return { status: 400, error: "Member-deals email isn't available on this account." };
  return {
    band,
    fields: { birthMonth: b.birthMonth as number, birthYear: b.birthYear as number, agreementAccepted: true, ...(versions as Record<AgreementField, string>) },
  };
}

// ── What the server says about a created or signed-in account ────────────────────────────────────────────────────────────────────────────────
export interface ServerAge {
  band: "youth" | "member" | "unknown";
  youth: boolean;
  marketingEmailAvailable: boolean;
  reviewSettings: boolean;
}
export function parseServerAge(raw: unknown): ServerAge | null {
  if (!isObj(raw) || (raw.band !== "youth" && raw.band !== "member" && raw.band !== "unknown")) return null;
  if (typeof raw.youth !== "boolean" || raw.youth !== (raw.band === "youth")) return null;
  if (typeof raw.marketingEmailAvailable !== "boolean" || typeof raw.reviewSettings !== "boolean") return null;
  return { band: raw.band, youth: raw.youth, marketingEmailAvailable: raw.marketingEmailAvailable, reviewSettings: raw.reviewSettings };
}
export interface ServerAgreement {
  status: string;
  acceptedAt: string;
  termsVersion: string;
  privacyVersion: string;
  membershipVersion: string;
}
export function parseServerAgreement(raw: unknown): ServerAgreement | null {
  if (!isObj(raw) || !isText(raw.status, 64) || typeof raw.acceptedAt !== "string" || Number.isNaN(Date.parse(raw.acceptedAt))) return null;
  for (const { field } of AGREEMENT_DOCS) if (typeof raw[field] !== "string" || !VERSION_RE.test(raw[field] as string)) return null;
  return { status: raw.status, acceptedAt: raw.acceptedAt, termsVersion: raw.termsVersion as string, privacyVersion: raw.privacyVersion as string, membershipVersion: raw.membershipVersion as string };
}

// ── Youth: no advertising cookies ────────────────────────────────────────────────────────────────────────────────────────────────────────────
// Set by the apps' own servers only from the backend's age band (register answer or the account's settings), on the shared session domain, and
// cleared when the backend says the member is not youth. While present, no advertising choice can be granted on either app. It holds no age.
export const YOUTH_ADS_OFF_COOKIE = "sg_youth_ads_off";
export const youthAdsOff = (cookies: string) => cookies.split(/;\s*/).some((c) => c === `${YOUTH_ADS_OFF_COOKIE}=1`);

// ── Essential-only mode (youth spec v1.1: 13-15 get essential/anonymous analytics only) ──────────────────────────────────────────────────────
// Non-essential measurement (GA4 and every advertising tag) is OFF when either holds:
//   - the server-set youth flag above (the backend's band for a signed-in 13-15 member), or
//   - this page load has been told so in memory, because the visitor just answered the age step with a 13-15 or under-13 birth date. That
//     answer is never written anywhere: no cookie, no storage, no request; it ends with the page.
// Adults and unknown ages are untouched. The apps implement the switch (no tag loads, GA's own ga-disable flag, identifier cookies cleared).
let suppressedThisPage = false;
export function suppressNonEssential(): void {
  suppressedThisPage = true;
}
export const nonEssentialSuppressed = (cookies: string) => suppressedThisPage || youthAdsOff(cookies);
/** Test-only. */
export function _resetSuppressionForTests(): void {
  suppressedThisPage = false;
}
/** The GA4 and advertising identifier cookies this site's own tags set (first-party): cleared on entering essential-only mode. */
export const ANALYTICS_IDENTIFIER_COOKIE = /^(_ga|_ga_[A-Za-z0-9]+|_gid|_gat(_[A-Za-z0-9]+)?|_gcl_[a-z]+|_fbp|_fbc)$/;
/** Every `document.cookie` assignment that expires `name` on this host and each parent domain (where the tag may have set it). */
export function expiryWrites(name: string, hostname: string): string[] {
  const parts = hostname.split(".");
  const out = [`${name}=; path=/; max-age=0`];
  for (let i = 0; i < parts.length - 1; i++) out.push(`${name}=; path=/; max-age=0; domain=.${parts.slice(i).join(".")}`);
  return out;
}

// ── The age answer (GET /api/auth/age in Root and Accounts) ──────────────────────────────────────────────────────────────────────────────────
// `anonymous`: no session (no candidate, or every candidate refused by the backend) -> the existing adult/optional-ads contract.
// `known`: ONE verified identity and its backend band. `unresolved`: a session exists but its band could not be established (backend outage,
// malformed answer, or two different verified identities): FAIL CLOSED, the page is essential-only. Anything unrecognised is `unresolved`.
export type AgeMode = "open" | "youth" | "unresolved";
export function ageModeOf(raw: unknown): AgeMode {
  if (isObj(raw) && raw.state === "anonymous") return "open";
  if (isObj(raw) && raw.state === "known" && typeof raw.youth === "boolean") return raw.youth ? "youth" : "open";
  return "unresolved";
}
